import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  DollarSign, AlertTriangle, RefreshCw, Download, Search, Eye, 
  Clock, CheckCircle, XCircle, ChevronRight, Filter, FileText,
  Ban, UserX, Pause, Play, Edit, MessageSquare, Calendar,
  ExternalLink, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlatformFinanceSummary, formatAUD } from '../lib/usePlatformFinanceSummary';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

const FinancialEnforcementDashboard = () => {
  const { api, user } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSubTab, setActiveSubTab] = useState('charges');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Charges state
  const [charges, setCharges] = useState([]);
  const [chargesTotal, setChargesTotal] = useState(0);
  const [chargeFilters, setChargeFilters] = useState({ status: '', collection_status: '', search: '' });
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  
  // Collections state
  const [collections, setCollections] = useState([]);
  const [collectionsSummary, setCollectionsSummary] = useState(null);
  
  // Disputes state
  const [disputes, setDisputes] = useState([]);
  const [disputeFilter, setDisputeFilter] = useState('');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeDetailLoading, setDisputeDetailLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [pendingResolution, setPendingResolution] = useState(null); // { type: 'no_charge' | 'require_payment' }
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({ event_type: '', actor: '', entity_id: '' });
  
  // Action modals
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustItemId, setAdjustItemId] = useState('');
  
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeReason, setFreezeReason] = useState('');
  
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveReason, setResolveReason] = useState('');
  
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  
  const [actionLoading, setActionLoading] = useState(false);

  // Finance Summary
  const [financeSummary, setFinanceSummary] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeRangeDays, setFinanceRangeDays] = useState(30);
  
  // Payment Detail Modal (for Recent Payments click)
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);
  const [providerMode, setProviderMode] = useState('DEMO');

  // Recent payment records — search + pagination (mirrors Users tab pattern)
  const [paymentsSearchQuery, setPaymentsSearchQuery] = useState('');
  const [paymentsPage, setPaymentsPage] = useState(1);
  const PAYMENTS_PAGE_SIZE = 10;

  // Fetch provider environment on mount  
  useEffect(() => {
    // Provider mode is determined from finance summary response
  }, [api]);

  const fetchPaymentDetails = useCallback(async (rowId) => {
    if (!rowId) {
      toast.error('Invalid payment identifier');
      return;
    }
    setPaymentDetailLoading(true);
    try {
      // Use the new row_id based endpoint for precise payment lookup
      const res = await api.get(`/platform-owner/finance/payment-details/${encodeURIComponent(rowId)}`);
      setSelectedPayment(res.data);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
      // DO NOT open stale modal on error
    } finally {
      setPaymentDetailLoading(false);
    }
  }, [api]);

  // Helper to format cents as AUD
  const formatCentsAUD = (cents) => {
    if (cents === null || cents === undefined) return '—';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Helper to format percentage
  const formatPct = (pct) => {
    if (pct === null || pct === undefined) return '—';
    const val = pct > 1 ? pct : pct * 100;
    return `${val.toFixed(1)}%`;
  };

  const fetchFinanceSummary = useCallback(async (rangeDays = 30) => {
    setFinanceLoading(true);
    try {
      const res = await api.get(`/platform-owner/finance/summary?range_days=${rangeDays}`);
      setFinanceSummary(res.data);
    } catch (error) {
      console.error('Error fetching finance summary:', error);
      toast.error('Failed to load finance data');
    } finally {
      setFinanceLoading(false);
    }
  }, [api]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const res = await api.get('/platform-owner/financial/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [api]);

  const fetchCharges = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (chargeFilters.status) params.append('status', chargeFilters.status);
      if (chargeFilters.collection_status) params.append('collection_status', chargeFilters.collection_status);
      if (chargeFilters.search) params.append('search', chargeFilters.search);
      params.append('limit', '100');
      
      const res = await api.get(`/platform-owner/financial/charges?${params}`);
      setCharges(res.data.charges || []);
      setChargesTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching charges:', error);
      toast.error('Failed to load charges');
    } finally {
      setLoading(false);
    }
  }, [api, chargeFilters]);

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/platform-owner/financial/collections');
      setCollections(res.data.charges || []);
      setCollectionsSummary(res.data.summary || null);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const url = disputeFilter 
        ? `/platform-owner/financial/disputes?status=${disputeFilter}`
        : '/platform-owner/financial/disputes';
      const res = await api.get(url);
      setDisputes(res.data.disputes || []);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoading(false);
    }
  }, [api, disputeFilter]);

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilters.event_type) params.append('event_type', auditFilters.event_type);
      if (auditFilters.actor) params.append('actor', auditFilters.actor);
      if (auditFilters.entity_id) params.append('entity_id', auditFilters.entity_id);
      params.append('limit', '200');
      
      const res = await api.get(`/platform-owner/financial/audit-logs?${params}`);
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [api, auditFilters]);

  useEffect(() => {
    fetchDashboardData();
    fetchFinanceSummary(financeRangeDays);
  }, [fetchDashboardData, fetchFinanceSummary, financeRangeDays]);

  useEffect(() => {
    if (activeSubTab === 'charges') fetchCharges();
    else if (activeSubTab === 'collections') fetchCollections();
    else if (activeSubTab === 'disputes') fetchDisputes();
    else if (activeSubTab === 'audit') fetchAuditLogs();
  }, [activeSubTab, fetchCharges, fetchCollections, fetchDisputes, fetchAuditLogs]);

  // View charge detail
  const viewChargeDetail = async (chargeId) => {
    try {
      const res = await api.get(`/platform-owner/financial/charges/${chargeId}`);
      setSelectedCharge(res.data);
      setShowChargeModal(true);
    } catch (error) {
      toast.error('Failed to load charge details');
    }
  };

  // Refresh top-level KPI summaries after any mutation
  const refreshSummaries = useCallback(() => {
    fetchDashboardData();
    fetchFinanceSummary(financeRangeDays);
  }, [fetchDashboardData, fetchFinanceSummary, financeRangeDays]);

  // Actions
  const handleExecutePayment = async (chargeId) => {
    if (!window.confirm('Execute off-session payment for this charge?')) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${chargeId}/execute`);
      toast.success('Payment execution initiated');
      fetchCharges();
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryPayment = async (chargeId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/collections/${chargeId}/retry-now`);
      toast.success('Retry initiated');
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFreeze = async () => {
    if (!selectedCharge || !freezeReason) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${selectedCharge.charge?.charge_id}/freeze`, {
        reason: freezeReason
      });
      toast.success('Charge frozen');
      setShowFreezeModal(false);
      setFreezeReason('');
      fetchCharges();
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to freeze');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfreeze = async (chargeId) => {
    if (!window.confirm('Unfreeze this charge and allow execution?')) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${chargeId}/unfreeze`, { resume_retries: true });
      toast.success('Charge unfrozen');
      fetchCharges();
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfreeze');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!selectedCharge || !resolveReason) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${selectedCharge.charge?.charge_id}/mark-resolved`, {
        reason: resolveReason
      });
      toast.success('Charge marked as resolved');
      setShowResolveModal(false);
      setResolveReason('');
      fetchCharges();
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark resolved');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustAmount = async () => {
    if (!selectedCharge || !adjustItemId || !adjustAmount || !adjustReason) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${selectedCharge.charge?.charge_id}/adjust`, {
        item_id: adjustItemId,
        new_amount_cents: Math.round(parseFloat(adjustAmount) * 100),
        reason: adjustReason
      });
      toast.success('Amount adjusted');
      setShowAdjustModal(false);
      setAdjustAmount('');
      setAdjustReason('');
      setAdjustItemId('');
      // Refresh charge detail
      viewChargeDetail(selectedCharge.charge?.charge_id);
      fetchCharges();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust amount');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedCharge || !noteText) return;
    setActionLoading(true);
    try {
      await api.post(`/platform-owner/financial/charges/${selectedCharge.charge?.charge_id}/add-note`, {
        note: noteText
      });
      toast.success('Note added');
      setShowNoteModal(false);
      setNoteText('');
      viewChargeDetail(selectedCharge.charge?.charge_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Suspend this user?')) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/suspend`, { reason: 'Failed payment enforcement' });
      toast.success('User suspended');
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/unsuspend`);
      toast.success('User unsuspended');
      fetchCollections();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsuspend user');
    } finally {
      setActionLoading(false);
    }
  };

  // P1: Fetch dispute details and open modal
  const handleOpenDisputeDetail = async (dispute) => {
    setDisputeDetailLoading(true);
    setSelectedDispute(dispute); // Show basic data immediately
    setShowDisputeModal(true);
    setResolutionNotes('');
    
    try {
      const res = await api.get(`/platform-owner/disputes/${dispute.dispute_id}`);
      setSelectedDispute(res.data.dispute);
    } catch (error) {
      console.error('Error fetching dispute details:', error);
      // Keep the basic dispute data if detail fetch fails
    } finally {
      setDisputeDetailLoading(false);
    }
  };
  
  // P1: Handle dispute resolution with confirmation
  const handleResolveDisputeFromModal = async () => {
    if (!pendingResolution || !selectedDispute) return;
    
    if (resolutionNotes.trim().length < 10) {
      toast.error('Resolution notes required (min 10 chars)');
      return;
    }
    
    setActionLoading(true);
    try {
      const endpoint = pendingResolution.type === 'no_charge' 
        ? `/platform-owner/disputes/${selectedDispute.dispute_id}/resolve-no-charge`
        : `/platform-owner/disputes/${selectedDispute.dispute_id}/require-payment`;
      
      await api.post(endpoint, { notes: resolutionNotes.trim() });
      
      toast.success(pendingResolution.type === 'no_charge' 
        ? 'Dispute resolved - no charges applied' 
        : 'Dispute resolved - payment required');
      
      setShowResolveConfirm(false);
      setShowDisputeModal(false);
      setSelectedDispute(null);
      setResolutionNotes('');
      setPendingResolution(null);
      fetchDisputes();
      refreshSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve dispute');
    } finally {
      setActionLoading(false);
    }
  };
  
  // P1: Initiate resolution (opens confirmation)
  const initiateResolution = (type) => {
    if (resolutionNotes.trim().length < 10) {
      toast.error('Resolution notes required (min 10 chars)');
      return;
    }
    setPendingResolution({ type });
    setShowResolveConfirm(true);
  };

  // Legacy dispute resolution (keeping for backwards compatibility with list buttons)
  const handleResolveDispute = async (disputeId, resolutionType) => {
    // Now opens the modal instead of using window.prompt
    const dispute = disputes.find(d => d.dispute_id === disputeId);
    if (dispute) {
      handleOpenDisputeDetail(dispute);
    }
  };

  // Exports
  const handleExport = async (type) => {
    try {
      const response = await api.get(`/platform-owner/financial/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'pending_admin_review': 'bg-yellow-500/20 text-yellow-400',
      'approved': 'bg-green-500/20 text-green-400',
      'rejected': 'bg-red-500/20 text-red-400',
      'disputed': 'bg-purple-500/20 text-purple-400',
      'cancelled': 'bg-slate-500/20 text-slate-400',
      'resolved': 'bg-blue-500/20 text-blue-400'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getCollectionStatusBadge = (status) => {
    const colors = {
      'pending_execution': 'bg-blue-500/20 text-blue-400',
      'succeeded': 'bg-green-500/20 text-green-400',
      'failed_retrying': 'bg-amber-500/20 text-amber-400',
      'failed_final': 'bg-red-500/20 text-red-400',
      'frozen_dispute': 'bg-purple-500/20 text-purple-400',
      'manually_resolved': 'bg-cyan-500/20 text-cyan-400'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-400';
  };

  // ============================================================================
  // ACTION STATE LABELS - UX Clarity Enhancement
  // Shows who needs to act next for each item
  // ============================================================================
  const getActionStateLabel = (item) => {
    // For charges - check specific states first
    if (item.status === 'pending_admin_review') {
      return { label: 'Action Required', color: 'bg-red-500/30 text-red-300 border-red-400/50', icon: 'alert' };
    }
    if (item.status === 'disputed' || item.frozen_for_dispute) {
      return { label: 'Under Review', color: 'bg-purple-500/30 text-purple-300 border-purple-400/50', icon: 'clock' };
    }
    if (item.collection_status === 'pending_execution') {
      return { label: 'Waiting on Renter', color: 'bg-blue-500/30 text-blue-300 border-blue-400/50', icon: 'clock' };
    }
    if (item.collection_status === 'failed_retrying') {
      return { label: 'Retry Pending', color: 'bg-amber-500/30 text-amber-300 border-amber-400/50', icon: 'retry' };
    }
    if (item.collection_status === 'failed_final') {
      return { label: 'Action Required', color: 'bg-red-500/30 text-red-300 border-red-400/50', icon: 'alert' };
    }
    if (item.collection_status === 'succeeded' || (item.status === 'approved' && !item.collection_status)) {
      return { label: 'Completed', color: 'bg-green-500/30 text-green-300 border-green-400/50', icon: 'check' };
    }
    if (item.status === 'rejected' || item.status === 'cancelled') {
      return { label: 'Closed', color: 'bg-slate-500/30 text-slate-300 border-slate-400/50', icon: 'check' };
    }
    // Default for pending/submitted items
    if (item.status === 'pending' || item.status === 'submitted') {
      return { label: 'Pending Review', color: 'bg-amber-500/30 text-amber-300 border-amber-400/50', icon: 'clock' };
    }
    return null;
  };

  // For disputes
  const getDisputeActionLabel = (dispute) => {
    if (dispute.status === 'open' || dispute.status === 'under_review') {
      return { label: 'Action Required', color: 'bg-red-500/30 text-red-300 border-red-400/50', icon: 'alert' };
    }
    if (dispute.status === 'awaiting_response') {
      return { label: 'Waiting on Response', color: 'bg-blue-500/30 text-blue-300 border-blue-400/50', icon: 'clock' };
    }
    if (dispute.status === 'resolved' || dispute.status === 'closed') {
      return { label: 'Completed', color: 'bg-green-500/30 text-green-300 border-green-400/50', icon: 'check' };
    }
    return null;
  };

  const LoadingSpinner = () => (
    <div className="flex justify-center py-12">
      <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
    </div>
  );

  // Header badge for the shell
  const headerBadge = (
    <Badge className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0">
      PLATFORM OWNER ONLY
    </Badge>
  );

  return (
    <PlatformOwnerAppShell 
      title="Financial Enforcement" 
      activeId="financial"
      headerBadge={headerBadge}
    >
      <HostDirectModeBanner variant="subtle" context="generic" className="mb-4" />
      {/* Summary KPIs */}
      {/* TOP KPIS - Using Finance Summary (Single Source of Truth) */}
      {financeLoading ? (
        <div className="flex justify-center py-4 mb-6">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
        </div>
      ) : financeSummary ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6">
          <Card className="bg-slate-800/80 border-slate-600/50">
            <CardContent className="p-3">
              <p className="text-xs text-slate-300">Total Revenue (GMV)</p>
              <p className="text-base sm:text-lg font-bold text-white" data-testid="kpi-total-revenue">
                  ${financeSummary.totals.total_charges_gross?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-900/70 border-emerald-500/50">
              <CardContent className="p-3">
                <p className="text-xs text-emerald-300">Platform Fees (Gross)</p>
                <p className="text-base sm:text-lg font-bold text-emerald-200" data-testid="kpi-platform-fees-gross">
                  ${financeSummary.totals.platform_fees_gross?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-900/60 border-red-500/50">
              <CardContent className="p-3">
                <p className="text-xs text-red-300">Processing Fees</p>
                <p className="text-base sm:text-lg font-bold text-red-200" data-testid="kpi-processing-fees">
                  ${financeSummary.totals.processing_fees_total?.toLocaleString('en-AU', {minimumFractionDigits: 2}) || financeSummary.totals.stripe_fees_total?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                </p>
              </CardContent>
            </Card>
            <Card className={`${financeSummary.totals.platform_fees_net >= 0 ? 'bg-green-900/60 border-green-500/50' : 'bg-amber-900/60 border-amber-500/50'}`}>
              <CardContent className="p-3">
                <p className={`text-xs ${financeSummary.totals.platform_fees_net >= 0 ? 'text-green-300' : 'text-amber-300'}`}>Platform Earnings (Net)</p>
                <p className={`text-base sm:text-lg font-bold ${financeSummary.totals.platform_fees_net >= 0 ? 'text-green-200' : 'text-amber-200'}`} data-testid="kpi-platform-fees-net">
                  ${financeSummary.totals.platform_fees_net?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-cyan-900/60 border-cyan-500/50">
              <CardContent className="p-3">
                <p className="text-xs text-cyan-300">Host Payouts</p>
                <p className="text-base sm:text-lg font-bold text-cyan-200" data-testid="kpi-host-payouts">
                  ${financeSummary.totals.host_payouts_total?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-purple-900/60 border-purple-500/50">
              <CardContent className="p-3">
                <p className="text-xs text-purple-300">Paid Bookings</p>
                <p className="text-base sm:text-lg font-bold text-purple-200" data-testid="kpi-paid-bookings">
                  {financeSummary.counts.paid_bookings}
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Error/No data state - show KPI cards with "—" values */
          <div className="mb-6">
            <div className="text-center text-amber-400/80 text-sm py-3 px-4 bg-amber-500/10 rounded-lg mb-4 flex items-center justify-center gap-2" data-testid="finance-error-banner">
              <AlertTriangle className="w-4 h-4" />
              Unable to load financial data. Please try again.
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchFinanceSummary(financeRangeDays)}
                className="text-amber-400 hover:text-amber-300 ml-2 h-7"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Retry
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              <Card className="bg-slate-800/50 border-slate-600/30">
                <CardContent className="p-3">
                  <p className="text-xs text-slate-400">Total Revenue (GMV)</p>
                  <p className="text-base sm:text-lg font-bold text-slate-500">—</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-900/30 border-emerald-500/20">
                <CardContent className="p-3">
                  <p className="text-xs text-emerald-400/60">Platform Fees (Gross)</p>
                  <p className="text-base sm:text-lg font-bold text-emerald-300/50">—</p>
                </CardContent>
              </Card>
              <Card className="bg-red-900/30 border-red-500/20">
                <CardContent className="p-3">
                  <p className="text-xs text-red-400/60">Processing Fees</p>
                  <p className="text-base sm:text-lg font-bold text-red-300/50">—</p>
                </CardContent>
              </Card>
              <Card className="bg-green-900/30 border-green-500/20">
                <CardContent className="p-3">
                  <p className="text-xs text-green-400/60">Platform Earnings (Net)</p>
                  <p className="text-base sm:text-lg font-bold text-green-300/50">—</p>
                </CardContent>
              </Card>
              <Card className="bg-cyan-900/30 border-cyan-500/20">
                <CardContent className="p-3">
                  <p className="text-xs text-cyan-400/60">Host Payouts</p>
                  <p className="text-base sm:text-lg font-bold text-cyan-300/50">—</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-900/30 border-purple-500/20">
                <CardContent className="p-3">
                  <p className="text-xs text-purple-400/60">Paid Bookings</p>
                  <p className="text-base sm:text-lg font-bold text-purple-300/50">—</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* FINANCE DETAIL SECTION - Recent Payments Table */}
        <Card className="bg-black/50 border-emerald-500/40 mb-6 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                <CardTitle className="text-base sm:text-lg text-emerald-200 font-semibold" data-testid="finance-section-title">
                  {isHostDirect ? 'Booking Value Tracking' : 'Revenue (Real-Time)'}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <select 
                  value={financeRangeDays}
                  onChange={(e) => setFinanceRangeDays(Number(e.target.value))}
                  className="bg-black/60 border border-white/30 rounded px-2 py-1.5 text-xs text-white font-medium"
                  data-testid="finance-range-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 1 year</option>
                </select>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fetchFinanceSummary(financeRangeDays)}
                  disabled={financeLoading}
                  className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                >
                  <RefreshCw className={`w-4 h-4 ${financeLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            <CardDescription className="text-emerald-300/80 text-sm mt-1 flex items-center gap-2 flex-wrap" data-testid="finance-section-subtitle">
              {isHostDirect ? (
                <span>Recorded host-direct booking values · {financeSummary?.counts?.paid_bookings || 0} booking{(financeSummary?.counts?.paid_bookings || 0) === 1 ? '' : 's'} confirmed by host</span>
              ) : (
                <>
                  <span>Live data • {financeSummary?.counts?.paid_bookings || 0} successful payments</span>
                  {financeSummary?.provider_mode && (
                    <Badge
                      className={`text-xs ${financeSummary.provider_mode === 'LIVE' ? 'bg-green-600/60 text-green-100 border-green-400/50' : 'bg-amber-600/60 text-amber-100 border-amber-400/50'}`}
                      data-testid="provider-mode-badge"
                    >
                      {financeSummary.payment_provider?.toUpperCase()} {financeSummary.provider_mode}
                    </Badge>
                  )}
                  {financeSummary?.delayed_payouts_enabled && (
                    <Badge className="text-xs bg-purple-600/60 text-purple-100 border-purple-400/50">
                      Delayed Payouts ON
                    </Badge>
                  )}
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {financeLoading ? (
              <div className="flex justify-center py-4">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
              </div>
            ) : financeSummary ? (
              <>
                {/* Revenue Cards - Gross + Net - Higher contrast backgrounds */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4">
                  <Card className="bg-slate-800/80 border-slate-600/50">
                    <CardContent className="p-3">
                      <p className="text-xs text-slate-300">Total Charges (Gross)</p>
                      <p className="text-base sm:text-lg font-bold text-white" data-testid="total-charges-gross">
                        ${financeSummary.totals.total_charges_gross?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-900/70 border-emerald-500/50">
                    <CardContent className="p-3">
                      <p className="text-xs text-emerald-300">Platform Fees (Gross)</p>
                      <p className="text-base sm:text-lg font-bold text-emerald-200" data-testid="platform-fees-gross">
                        ${financeSummary.totals.platform_fees_gross?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-900/60 border-red-500/50">
                    <CardContent className="p-3">
                      <p className="text-xs text-red-300">Processing Fees</p>
                      <p className="text-base sm:text-lg font-bold text-red-200" data-testid="processing-fees">
                        ${(financeSummary.totals.processing_fees_total || financeSummary.totals.stripe_fees_total)?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={`${financeSummary.totals.platform_fees_net >= 0 ? 'bg-green-900/60 border-green-500/50' : 'bg-amber-900/60 border-amber-500/50'}`}>
                    <CardContent className="p-3">
                      <p className={`text-xs ${financeSummary.totals.platform_fees_net >= 0 ? 'text-green-300' : 'text-amber-300'}`}>
                        Platform Fees (Net)
                      </p>
                      <p className={`text-base sm:text-lg font-bold ${financeSummary.totals.platform_fees_net >= 0 ? 'text-green-200' : 'text-amber-200'}`} data-testid="platform-fees-net">
                        ${financeSummary.totals.platform_fees_net?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-cyan-900/60 border-cyan-500/50">
                    <CardContent className="p-3">
                      <p className="text-xs text-cyan-300">Host Payouts</p>
                      <p className="text-base sm:text-lg font-bold text-cyan-200" data-testid="host-payouts">
                        ${financeSummary.totals.host_payouts_total?.toLocaleString('en-AU', {minimumFractionDigits: 2})}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-900/60 border-purple-500/50">
                    <CardContent className="p-3">
                      <p className="text-xs text-purple-300">Paid Bookings</p>
                      <p className="text-base sm:text-lg font-bold text-purple-200" data-testid="paid-bookings-count">
                        {financeSummary.counts.paid_bookings}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Payments Table - Improved readability with solid row backgrounds
                    Iter119: search by booking ID + pagination (10/page) replacing the
                    hardcoded "last 10" view. Page size is fixed; counts come from
                    backend payload (no hardcoded totals). */}
                {financeSummary.recent && financeSummary.recent.length > 0 && (() => {
                  const allRecent = financeSummary.recent || [];
                  const q = (paymentsSearchQuery || '').trim().toLowerCase();
                  const filtered = q
                    ? allRecent.filter((p) => {
                        const bid = (p.booking_id || '').toLowerCase();
                        const cid = (p.charge_id || '').toLowerCase();
                        return bid.includes(q) || cid.includes(q);
                      })
                    : allRecent;
                  const totalFiltered = filtered.length;
                  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAYMENTS_PAGE_SIZE));
                  const safePage = Math.min(paymentsPage, totalPages);
                  const startIdx = totalFiltered === 0 ? 0 : (safePage - 1) * PAYMENTS_PAGE_SIZE;
                  const endIdx = Math.min(startIdx + PAYMENTS_PAGE_SIZE, totalFiltered);
                  const visiblePayments = filtered.slice(startIdx, endIdx);
                  const sectionTitle = isHostDirect ? 'Booking Payment Records' : 'Recent Payments';
                  return (
                  <div className="mt-4" data-testid="recent-payments-section">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <h4 className="text-sm font-semibold text-white" data-testid="recent-payments-title">
                        {sectionTitle}
                      </h4>
                      <input
                        type="search"
                        value={paymentsSearchQuery}
                        onChange={(e) => { setPaymentsSearchQuery(e.target.value); setPaymentsPage(1); }}
                        placeholder="Search by booking ID…"
                        data-testid="recent-payments-search-input"
                        className="w-full sm:w-72 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                    </div>
                    {totalFiltered > 0 && (
                      <p className="text-xs text-slate-400 mb-2" data-testid="recent-payments-pagination-summary">
                        Showing {startIdx + 1}–{endIdx} of {totalFiltered} record{totalFiltered === 1 ? '' : 's'}
                        {q ? ` (filtered from ${allRecent.length})` : ''}
                      </p>
                    )}
                    <div className="overflow-x-auto rounded-lg bg-black/40">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-800/80 border-b border-slate-600/50">
                            <th className="text-left py-2.5 px-3 text-slate-200 font-semibold">Booking</th>
                            <th className="text-right py-2.5 px-3 text-slate-200 font-semibold">Charge</th>
                            <th className="text-right py-2.5 px-3 text-slate-200 font-semibold">Platform Fee</th>
                            <th className="text-right py-2.5 px-3 text-slate-200 font-semibold">Processing Fee</th>
                            <th className="text-right py-2.5 px-3 text-slate-200 font-semibold">Host Payout</th>
                            <th className="text-center py-2.5 px-3 text-slate-200 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePayments.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-6 text-center text-white/60" data-testid="recent-payments-empty-state">
                                {q ? `No records match "${paymentsSearchQuery}"` : 'No payment records yet'}
                              </td>
                            </tr>
                          ) : visiblePayments.map((payment, idx) => {
                            // Determine bond lifecycle state and display
                            const getBondDisplay = () => {
                              if (!payment.is_bond) return null;
                              
                              const bondStatus = payment.bond_status || 'authorized';
                              const bondType = payment.payment_type || 'bond';
                              const isExtensionBond = bondType === 'extension_bond';
                              const lifecycle = payment.bond_lifecycle || {};
                              const isSuperseded = lifecycle.is_superseded || bondStatus === 'superseded' || bondStatus === 'superseded_for_extension';
                              const isActive = lifecycle.is_active || (!isSuperseded && bondStatus === 'authorized');
                              
                              let badgeClass = '';
                              let badgeText = '';
                              let moneyDirection = '';
                              let lifecycleNote = lifecycle.lifecycle_note || '';
                              
                              // Superseded bonds get special treatment
                              if (isSuperseded) {
                                badgeClass = 'bg-slate-700/60 text-slate-400 border border-slate-600/50 line-through';
                                badgeText = isExtensionBond ? 'EXT BOND (VOID)' : 'ORIG BOND (SUPERSEDED)';
                                moneyDirection = 'Replaced by extension bond';
                                lifecycleNote = 'Authorization cancelled — no funds held';
                              } else {
                                switch (bondStatus) {
                                  case 'authorized':
                                    badgeClass = isExtensionBond 
                                      ? 'bg-purple-600/50 text-purple-100 border-2 border-purple-400/70 font-semibold'
                                      : 'bg-amber-600/40 text-amber-200 border border-amber-500/50';
                                    badgeText = isExtensionBond ? 'EXT BOND ACTIVE' : 'BOND HOLD';
                                    moneyDirection = isExtensionBond ? 'Current active bond' : 'Held from renter';
                                    break;
                                  case 'captured':
                                    badgeClass = 'bg-red-600/40 text-red-200 border border-red-500/50';
                                    badgeText = 'BOND CAPTURED';
                                    moneyDirection = 'Captured from renter';
                                    break;
                                  case 'partially_captured':
                                    badgeClass = 'bg-orange-600/40 text-orange-200 border border-orange-500/50';
                                    badgeText = 'PARTIAL CAPTURE';
                                    moneyDirection = 'Partial charge';
                                    break;
                                  case 'released':
                                    badgeClass = 'bg-green-600/40 text-green-200 border border-green-500/50';
                                    badgeText = 'BOND RELEASED';
                                    moneyDirection = 'Returned to renter';
                                    break;
                                  case 'cancelled':
                                    badgeClass = 'bg-slate-600/40 text-slate-300 border border-slate-500/50';
                                    badgeText = 'CANCELLED';
                                    moneyDirection = 'Authorization voided';
                                    break;
                                  case 'pending':
                                    badgeClass = 'bg-blue-600/40 text-blue-200 border border-blue-500/50';
                                    badgeText = 'PENDING';
                                    moneyDirection = 'Awaiting authorization';
                                    break;
                                  default:
                                    badgeClass = 'bg-slate-600/40 text-slate-300 border border-slate-500/50';
                                    badgeText = bondStatus.toUpperCase();
                                    moneyDirection = 'Unknown state';
                                }
                              }
                              
                              return { badgeClass, badgeText, moneyDirection, isSuperseded, isActive, lifecycleNote };
                            };
                            
                            const bondDisplay = getBondDisplay();
                            
                            return (
                            <tr 
                              key={payment.row_id || payment.charge_id || idx} 
                              className={`border-b border-slate-700/50 ${
                                payment.is_bond 
                                  ? bondDisplay?.isSuperseded
                                    ? 'bg-slate-900/60 opacity-70'  // Superseded bonds are dimmed
                                    : payment.bond_status === 'captured' || payment.bond_status === 'partially_captured'
                                      ? 'bg-red-900/20'
                                      : payment.bond_status === 'released'
                                        ? 'bg-green-900/20'
                                        : bondDisplay?.isActive
                                          ? 'bg-purple-900/25 border-l-2 border-purple-500'  // Active bond highlighted
                                          : 'bg-amber-900/20'
                                  : idx % 2 === 0 ? 'bg-slate-900/50' : 'bg-slate-800/30'
                              } hover:bg-slate-700/40 transition-colors cursor-pointer`}
                              onClick={() => payment.row_id && fetchPaymentDetails(payment.row_id)}
                              data-testid={`payment-row-${payment.row_id || idx}`}
                            >
                              <td className="py-2.5 px-3 text-white">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-mono text-slate-200 ${bondDisplay?.isSuperseded ? 'line-through opacity-60' : ''}`}>
                                    {payment.booking_id?.substring(0, 15) || 'N/A'}...
                                  </span>
                                  {payment.is_bond && (
                                    <Badge className={`text-xs ${bondDisplay.badgeClass}`}>
                                      {bondDisplay.badgeText}
                                    </Badge>
                                  )}
                                  {!payment.is_bond && payment.is_host_direct && (
                                    <Badge
                                      className="text-[10px] bg-amber-600/30 text-amber-200 border border-amber-500/40"
                                      data-testid={`host-direct-badge-${payment.row_id || idx}`}
                                      title="Host received payment directly from renter (off-platform settlement)"
                                    >
                                      MANUAL (HOST DIRECT)
                                    </Badge>
                                  )}
                                  {!payment.is_bond && !payment.is_host_direct && (
                                    <Badge className="text-[10px] bg-blue-600/30 text-blue-300 border border-blue-500/40">
                                      RENTAL
                                    </Badge>
                                  )}
                                  {bondDisplay?.isActive && (
                                    <span className="text-[10px] bg-purple-500/30 text-purple-300 px-1.5 py-0.5 rounded">
                                      CURRENT
                                    </span>
                                  )}
                                </div>
                                <span className="text-slate-400 text-xs">{new Date(payment.created_at).toLocaleDateString()}</span>
                                {payment.is_bond && (
                                  <div className="text-slate-500 text-xs mt-0.5">
                                    • {bondDisplay.moneyDirection}
                                    {bondDisplay.isSuperseded && (
                                      <span className="ml-1 text-slate-400 italic">(no additional funds held)</span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="text-right py-2.5 px-3 text-white font-medium">
                                <span className={`${payment.is_bond ? 'text-amber-300' : ''} ${bondDisplay?.isSuperseded ? 'line-through opacity-50' : ''}`}>
                                  ${payment.charge_gross?.toFixed(2)}
                                </span>
                                {payment.is_bond && (
                                  <div className="text-xs text-slate-500">
                                    {bondDisplay?.isSuperseded ? '(void)' :
                                     payment.bond_status === 'released' ? '(released)' : 
                                     payment.bond_status === 'captured' ? '(captured)' : 
                                     payment.bond_status === 'partially_captured' ? '(partial capture)' : 
                                     bondDisplay?.isActive ? '(held - authorised)' :
                                     '(held)'}
                                  </div>
                                )}
                              </td>
                              <td className="text-right py-2.5 px-3 text-emerald-300 font-medium">
                                {payment.is_bond ? (
                                  <span className="text-slate-600 text-[10px] italic" title="Bonds are escrow holds — not revenue">—</span>
                                ) : payment.is_host_direct ? (
                                  <span
                                    className="text-slate-500 text-[10px] italic"
                                    title="Host-direct settlement — platform takes no cut"
                                  >$0.00</span>
                                ) : (
                                  `$${payment.platform_fee?.toFixed(2)}`
                                )}
                              </td>
                              <td className="text-right py-2.5 px-3 text-red-300 font-medium">
                                {payment.is_bond ? (
                                  <span className="text-slate-600 text-[10px] italic" title="Bonds are escrow holds — not revenue">—</span>
                                ) : payment.is_host_direct ? (
                                  <span
                                    className="text-slate-500 text-[10px] italic"
                                    title="Off-platform — no provider processing fee"
                                  >—</span>
                                ) : (
                                  `$${(payment.processing_fee || payment.stripe_fee)?.toFixed(2)}`
                                )}
                              </td>
                              <td className="text-right py-2.5 px-3 text-cyan-300 font-medium">
                                {payment.is_bond ? (
                                  <span className="text-slate-600 text-[10px] italic" title="Bonds are escrow holds — not revenue">—</span>
                                ) : (
                                  `$${payment.host_payout?.toFixed(2)}`
                                )}
                              </td>
                              <td className="text-center py-2.5 px-3">
                                {payment.is_bond ? (
                                  <Badge className={`text-xs font-medium ${bondDisplay.badgeClass}`}>
                                    {bondDisplay.badgeText}
                                  </Badge>
                                ) : payment.is_host_direct ? (
                                  <Badge
                                    className="text-xs font-medium bg-amber-600/40 text-amber-200 border border-amber-500/50"
                                    data-testid={`host-direct-status-${payment.row_id || idx}`}
                                  >
                                    Confirmed (Manual)
                                  </Badge>
                                ) : (
                                  <Badge className={`text-xs font-medium ${payment.status === 'succeeded' ? 'bg-green-600/40 text-green-200 border border-green-500/50' : 'bg-amber-600/40 text-amber-200 border border-amber-500/50'}`}>
                                    {payment.status}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10" data-testid="recent-payments-pagination-controls">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage <= 1}
                          onClick={() => setPaymentsPage((p) => Math.max(1, p - 1))}
                          data-testid="recent-payments-prev-btn"
                          className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-40"
                        >
                          Previous
                        </Button>
                        <span className="text-xs text-white/60" data-testid="recent-payments-page-indicator">
                          Page {safePage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage >= totalPages}
                          onClick={() => setPaymentsPage((p) => Math.min(totalPages, p + 1))}
                          data-testid="recent-payments-next-btn"
                          className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-40"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2 text-center">Click a row to view full payment details</p>
                  </div>
                  );
                })()}
              </>
            ) : (
              <p className="text-white/60 text-center py-4">No payment data available</p>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 mb-4 flex flex-wrap gap-1">
            <TabsTrigger value="charges" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm" data-testid="charges-subtab">
              <DollarSign className="w-4 h-4 mr-1" /> Charges
            </TabsTrigger>
            <TabsTrigger value="collections" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm" data-testid="collections-subtab">
              <AlertTriangle className="w-4 h-4 mr-1" /> Collections
            </TabsTrigger>
            <TabsTrigger value="disputes" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm" data-testid="disputes-subtab">
              <Ban className="w-4 h-4 mr-1" /> Disputes
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm" data-testid="audit-subtab">
              <FileText className="w-4 h-4 mr-1" /> Audit & Reports
            </TabsTrigger>
          </TabsList>

          {/* CHARGES TAB */}
          <TabsContent value="charges" data-testid="charges-tab-content">
            {/* Filters */}
            <Card className="bg-white/5 border-white/10 mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-white/60 mb-1 block">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        placeholder="Booking ID, Charge ID..."
                        value={chargeFilters.search}
                        onChange={(e) => setChargeFilters({ ...chargeFilters, search: e.target.value })}
                        className="pl-10 bg-white/10 border-white/20 h-9"
                        data-testid="charge-search-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Status</label>
                    <select
                      value={chargeFilters.status}
                      onChange={(e) => setChargeFilters({ ...chargeFilters, status: e.target.value })}
                      className="h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-sm"
                      data-testid="charge-status-filter"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending_admin_review">Pending Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="disputed">Disputed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Collection</label>
                    <select
                      value={chargeFilters.collection_status}
                      onChange={(e) => setChargeFilters({ ...chargeFilters, collection_status: e.target.value })}
                      className="h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-sm"
                      data-testid="charge-collection-filter"
                    >
                      <option value="">All</option>
                      <option value="pending_execution">Pending</option>
                      <option value="succeeded">Paid</option>
                      <option value="failed_retrying">Retrying</option>
                      <option value="failed_final">Failed Final</option>
                      <option value="frozen_dispute">Frozen</option>
                    </select>
                  </div>
                  <Button onClick={fetchCharges} variant="outline" className="h-9 border-white/20">
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Charges List */}
            {loading ? <LoadingSpinner /> : (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Charges ({chargesTotal})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {charges.length === 0 ? (
                    <p className="text-center py-8 text-white/60">No charges found</p>
                  ) : (
                    <div className="space-y-2">
                      {charges.map((charge) => {
                        const actionState = getActionStateLabel(charge);
                        return (
                        <div 
                          key={charge.charge_id}
                          className="p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                          onClick={() => viewChargeDetail(charge.charge_id)}
                          data-testid={`charge-row-${charge.charge_id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-white/80">#{charge.charge_id?.slice(0, 12)}</span>
                                <Badge className={`text-[10px] ${getStatusBadge(charge.status)}`}>{charge.status}</Badge>
                                <Badge className={`text-[10px] ${getCollectionStatusBadge(charge.collection_status)}`}>{charge.collection_status}</Badge>
                                {charge.frozen_for_dispute && <Badge className="text-[10px] bg-purple-500/30 text-purple-300">FROZEN</Badge>}
                                {/* Action State Label - UX Clarity */}
                                {actionState && (
                                  <Badge className={`text-[10px] font-semibold ${actionState.color}`} data-testid="action-state-badge">
                                    {actionState.icon === 'alert' && '⚠ '}{actionState.label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-white/60 mt-1">
                                {charge.renter?.full_name || 'Unknown'} • {charge.vehicle?.make} {charge.vehicle?.model} • {charge.item_count} items
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-sm">${charge.total_dollars?.toFixed(2)}</span>
                              <Eye className="w-4 h-4 text-white/40" />
                            </div>
                          </div>
                        </div>
                      );})}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* COLLECTIONS TAB */}
          <TabsContent value="collections" data-testid="collections-tab-content">
            {/* Collections KPIs */}
            {collectionsSummary && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-red-400">Total Unpaid</p>
                    <p className="text-lg font-bold text-red-400">${collectionsSummary.total_unpaid_dollars?.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-500/10 border-amber-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-amber-400">Retrying</p>
                    <p className="text-lg font-bold text-amber-400">{collectionsSummary.retrying?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-red-400">Final Failed</p>
                    <p className="text-lg font-bold text-red-400">{collectionsSummary.failed_final?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-purple-400">Frozen (Disputed)</p>
                    <p className="text-lg font-bold text-purple-400">{collectionsSummary.frozen_dispute?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-blue-400">Needs Action</p>
                    <p className="text-lg font-bold text-blue-400">{collectionsSummary.needs_action?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-500/10 border-slate-500/30">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-400">Users Suspended</p>
                    <p className="text-lg font-bold">{collectionsSummary.suspended_users || 0}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Collections Queue */}
            {loading ? <LoadingSpinner /> : (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Collections Queue ({collections.length})</span>
                    <Button onClick={fetchCollections} variant="ghost" size="sm" className="h-8">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {collections.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                      <p className="text-white/60">No pending collections</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {collections.map((item) => (
                        <div 
                          key={item.charge_id}
                          className={`p-4 rounded-lg border ${
                            item.collection_status === 'failed_final' ? 'bg-red-500/10 border-red-500/30' :
                            item.collection_status === 'frozen_dispute' ? 'bg-purple-500/10 border-purple-500/30' :
                            'bg-amber-500/10 border-amber-500/30'
                          }`}
                          data-testid={`collection-item-${item.charge_id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-mono text-xs">#{item.charge_id?.slice(0, 12)}</span>
                                <Badge className={`text-[10px] ${getCollectionStatusBadge(item.collection_status)}`}>
                                  {item.collection_status?.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </div>
                              <p className="text-sm font-medium">{item.renter?.full_name || 'Unknown'}</p>
                              <p className="text-xs text-white/60">{item.renter?.email}</p>
                              <div className="flex flex-wrap gap-3 mt-2 text-xs text-white/60">
                                <span>Retry #{item.retry_count || 0}</span>
                                {item.next_retry_at && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Next: {new Date(item.next_retry_at).toLocaleDateString()}
                                  </span>
                                )}
                                {item.last_payment_error_code && (
                                  <span className="text-red-400">Error: {item.last_payment_error_code}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-lg font-bold">${item.total_dollars?.toFixed(2)}</span>
                              <div className="flex flex-wrap gap-2">
                                {item.collection_status === 'frozen_dispute' ? (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="h-7 text-xs border-purple-500/50 text-purple-400"
                                    onClick={() => handleUnfreeze(item.charge_id)}
                                  >
                                    <Play className="w-3 h-3 mr-1" /> Unfreeze
                                  </Button>
                                ) : (
                                  <>
                                    <Button 
                                      size="sm" 
                                      className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                                      onClick={() => handleRetryPayment(item.charge_id)}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" /> Retry
                                    </Button>
                                    {item.renter?.is_suspended ? (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="h-7 text-xs border-green-500/50 text-green-400"
                                        onClick={() => handleUnsuspendUser(item.renter_id)}
                                      >
                                        Unsuspend
                                      </Button>
                                    ) : (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="h-7 text-xs border-red-500/50 text-red-400"
                                        onClick={() => handleSuspendUser(item.renter_id)}
                                      >
                                        <UserX className="w-3 h-3 mr-1" /> Suspend
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* DISPUTES TAB */}
          <TabsContent value="disputes" data-testid="disputes-tab-content">
            {/* Dispute Filter */}
            <Card className="bg-white/5 border-white/10 mb-4">
              <CardContent className="p-4 flex flex-wrap gap-3 items-center">
                <select
                  value={disputeFilter}
                  onChange={(e) => setDisputeFilter(e.target.value)}
                  className="h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-sm"
                  data-testid="dispute-status-filter"
                >
                  <option value="">All Disputes</option>
                  <option value="open">Open</option>
                  <option value="investigating">Under Investigation</option>
                  <option value="resolved">Resolved</option>
                </select>
                <Button onClick={fetchDisputes} variant="outline" className="h-9 border-white/20">
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </CardContent>
            </Card>

            {/* Disputes List */}
            {loading ? <LoadingSpinner /> : (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm">Charge Disputes ({disputes.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {disputes.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                      <p className="text-white/60">No disputes found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {disputes.map((dispute) => {
                        const disputeAction = getDisputeActionLabel(dispute);
                        return (
                        <div 
                          key={dispute.dispute_id}
                          onClick={() => handleOpenDisputeDetail(dispute)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.01] hover:shadow-lg ${
                            dispute.status === 'open' ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20' :
                            dispute.status === 'investigating' ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20' :
                            'bg-green-500/10 border-green-500/30 hover:bg-green-500/20'
                          }`}
                          data-testid={`dispute-row-${dispute.dispute_id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <span className="font-mono text-xs">#{dispute.dispute_id?.slice(0, 16)}</span>
                                <Badge className={`text-[10px] ${
                                  dispute.status === 'open' ? 'bg-red-500/20 text-red-400' :
                                  dispute.status === 'investigating' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-green-500/20 text-green-400'
                                }`}>
                                  {dispute.status?.toUpperCase()}
                                </Badge>
                                {dispute.charge?.frozen_for_dispute && (
                                  <Badge className="text-[10px] bg-purple-500/30 text-purple-300">EXECUTION FROZEN</Badge>
                                )}
                                {/* Action State Label - UX Clarity */}
                                {disputeAction && (
                                  <Badge className={`text-[10px] font-semibold ${disputeAction.color}`} data-testid="dispute-action-badge">
                                    {disputeAction.icon === 'alert' && '⚠ '}{disputeAction.label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm mb-1"><strong>Renter:</strong> {dispute.renter?.full_name} ({dispute.renter?.email})</p>
                              <p className="text-xs text-white/70 mb-2 line-clamp-1"><strong>Reason:</strong> {dispute.reason}</p>
                              <div className="flex flex-wrap gap-4 text-xs text-white/60">
                                <span>Amount: <strong className="text-white">${dispute.total_dollars?.toFixed(2)}</strong></span>
                                <span>Booking: #{dispute.booking_id?.slice(0, 8)}</span>
                                <span>Category: <strong className="text-white capitalize">{dispute.category || 'Other'}</strong></span>
                                {dispute.sla_hours_elapsed && (
                                  <span className={dispute.sla_hours_elapsed > 48 ? 'text-red-400' : ''}>
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {dispute.sla_hours_elapsed?.toFixed(1)}h elapsed
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm"
                                variant="ghost"
                                className="h-8 text-xs text-white/70 hover:text-white"
                                onClick={(e) => { e.stopPropagation(); handleOpenDisputeDetail(dispute); }}
                                data-testid={`view-dispute-btn-${dispute.dispute_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" /> View Details
                              </Button>
                            </div>
                          </div>
                          {dispute.resolution && (
                            <div className="mt-3 pt-3 border-t border-white/10 text-xs">
                              <span className="text-white/60">Resolution:</span> <span className="font-medium">{dispute.resolution}</span>
                              {dispute.resolution_notes && <span className="text-white/60 ml-2">- {dispute.resolution_notes?.slice(0, 50)}...</span>}
                            </div>
                          )}
                        </div>
                      );})}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AUDIT & REPORTS TAB */}
          <TabsContent value="audit" data-testid="audit-tab-content">
            {/* Export Buttons */}
            <Card className="bg-white/5 border-white/10 mb-4">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm">Export Reports</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => handleExport('collections')} variant="outline" className="border-white/20" data-testid="export-collections-btn">
                    <Download className="w-4 h-4 mr-2" /> Collections Export (CSV)
                  </Button>
                  <Button onClick={() => handleExport('disputes')} variant="outline" className="border-white/20" data-testid="export-disputes-btn">
                    <Download className="w-4 h-4 mr-2" /> Disputes Export (CSV)
                  </Button>
                  <Button onClick={() => handleExport('charge-ledger')} variant="outline" className="border-white/20" data-testid="export-ledger-btn">
                    <Download className="w-4 h-4 mr-2" /> Charge Ledger (CSV)
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Log Filters */}
            <Card className="bg-white/5 border-white/10 mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Event Type</label>
                    <select
                      value={auditFilters.event_type}
                      onChange={(e) => setAuditFilters({ ...auditFilters, event_type: e.target.value })}
                      className="h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-sm"
                    >
                      <option value="">All Events</option>
                      <option value="payment_retry_attempted">Payment Retry</option>
                      <option value="charge_frozen_for_dispute">Charge Frozen</option>
                      <option value="charge_unfrozen">Charge Unfrozen</option>
                      <option value="charge_dispute_opened">Dispute Opened</option>
                      <option value="charge_dispute_resolved">Dispute Resolved</option>
                      <option value="charge_amount_adjusted">Amount Adjusted</option>
                      <option value="user_suspended">User Suspended</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-xs text-white/60 mb-1 block">Actor</label>
                    <Input
                      placeholder="User ID or email..."
                      value={auditFilters.actor}
                      onChange={(e) => setAuditFilters({ ...auditFilters, actor: e.target.value })}
                      className="h-9 bg-white/10 border-white/20"
                    />
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-xs text-white/60 mb-1 block">Entity ID</label>
                    <Input
                      placeholder="Charge ID, Booking ID..."
                      value={auditFilters.entity_id}
                      onChange={(e) => setAuditFilters({ ...auditFilters, entity_id: e.target.value })}
                      className="h-9 bg-white/10 border-white/20"
                    />
                  </div>
                  <Button onClick={fetchAuditLogs} variant="outline" className="h-9 border-white/20">
                    <Search className="w-4 h-4 mr-1" /> Search
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Logs */}
            {loading ? <LoadingSpinner /> : (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm">Audit Logs ({auditLogs.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  {auditLogs.length === 0 ? (
                    <p className="text-center py-8 text-white/60">No audit logs found</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {auditLogs.map((log, idx) => (
                        <div key={idx} className="p-3 bg-white/5 rounded-lg text-xs" data-testid={`audit-log-${idx}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                            <Badge variant="outline" className="border-white/20 text-white/80 w-fit text-[10px]">
                              {log.action}
                            </Badge>
                            <span className="text-white/50">{new Date(log.timestamp).toLocaleString()}</span>
                          </div>
                          <p className="text-white/70">
                            <span className="text-white/50">Actor:</span> {log.user_email || log.user_id || 'system'}
                          </p>
                          <p className="text-white/70">
                            <span className="text-white/50">Entity:</span> {log.entity_id}
                          </p>
                          {log.details && (
                            <p className="text-white/50 mt-1 truncate">
                              {JSON.stringify(log.details).slice(0, 100)}...
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Charge Detail Modal */}
        <Dialog open={showChargeModal} onOpenChange={setShowChargeModal}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Charge Details
              </DialogTitle>
            </DialogHeader>
            {selectedCharge && (
              <div className="space-y-4">
                {/* Charge Summary */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-white/60 text-xs">Charge ID</p>
                        <p className="font-mono">{selectedCharge.charge?.charge_id?.slice(0, 16)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs">Status</p>
                        <Badge className={getStatusBadge(selectedCharge.charge?.status)}>{selectedCharge.charge?.status}</Badge>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs">Collection</p>
                        <Badge className={getCollectionStatusBadge(selectedCharge.charge?.collection_status)}>{selectedCharge.charge?.collection_status}</Badge>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs">Total</p>
                        <p className="text-xl font-bold">${(selectedCharge.charge?.subtotal_cents / 100)?.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Parties */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <p className="text-xs text-white/60 mb-2">Renter</p>
                      <p className="font-medium">{selectedCharge.renter?.full_name}</p>
                      <p className="text-sm text-white/70">{selectedCharge.renter?.email}</p>
                      {selectedCharge.renter?.is_suspended && <Badge className="mt-1 bg-red-500/20 text-red-400">SUSPENDED</Badge>}
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <p className="text-xs text-white/60 mb-2">Owner</p>
                      <p className="font-medium">{selectedCharge.owner?.full_name}</p>
                      <p className="text-sm text-white/70">{selectedCharge.owner?.email}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Items */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-sm">Charge Items</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="space-y-2">
                      {selectedCharge.charge?.items?.map((item, idx) => (
                        <div key={idx} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                          <div>
                            <Badge variant="outline" className="border-white/20 text-xs mb-1">{item.charge_type}</Badge>
                            <p className="text-sm">{item.description || 'No description'}</p>
                            <p className="text-xs text-white/50">Status: {item.status}</p>
                            {item.adjusted_at && (
                              <p className="text-xs text-amber-400">Adjusted: ${item.previous_amount_cents/100} → ${item.amount_cents/100}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold">${(item.amount_cents / 100)?.toFixed(2)}</p>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 text-xs"
                              onClick={() => {
                                setAdjustItemId(item.item_id);
                                setAdjustAmount((item.amount_cents / 100).toFixed(2));
                                setShowAdjustModal(true);
                              }}
                            >
                              <Edit className="w-3 h-3 mr-1" /> Adjust
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Dispute Info */}
                {selectedCharge.dispute && (
                  <Card className="bg-purple-500/10 border-purple-500/30">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium text-purple-400 mb-2">Active Dispute</p>
                      <p className="text-sm"><strong>Status:</strong> {selectedCharge.dispute.status}</p>
                      <p className="text-sm"><strong>Reason:</strong> {selectedCharge.dispute.reason}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {selectedCharge.charge?.status === 'approved' && !selectedCharge.charge?.frozen_for_dispute && (
                    <Button onClick={() => handleExecutePayment(selectedCharge.charge?.charge_id)} className="bg-green-600 hover:bg-green-700">
                      <DollarSign className="w-4 h-4 mr-1" /> Execute Payment
                    </Button>
                  )}
                  {!selectedCharge.charge?.frozen_for_dispute ? (
                    <Button onClick={() => setShowFreezeModal(true)} variant="outline" className="border-purple-500/50 text-purple-400">
                      <Pause className="w-4 h-4 mr-1" /> Freeze
                    </Button>
                  ) : (
                    <Button onClick={() => handleUnfreeze(selectedCharge.charge?.charge_id)} variant="outline" className="border-green-500/50 text-green-400">
                      <Play className="w-4 h-4 mr-1" /> Unfreeze
                    </Button>
                  )}
                  <Button onClick={() => setShowResolveModal(true)} variant="outline" className="border-cyan-500/50 text-cyan-400">
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark Resolved
                  </Button>
                  <Button onClick={() => setShowNoteModal(true)} variant="outline" className="border-white/20">
                    <MessageSquare className="w-4 h-4 mr-1" /> Add Note
                  </Button>
                </div>

                {/* Internal Notes */}
                {selectedCharge.charge?.internal_notes?.length > 0 && (
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-sm">Internal Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="space-y-2">
                        {selectedCharge.charge.internal_notes.map((note, idx) => (
                          <div key={idx} className="p-2 bg-white/5 rounded text-xs">
                            <p className="text-white/60">{note.added_by_email} - {new Date(note.added_at).toLocaleString()}</p>
                            <p>{note.text}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Audit Logs */}
                {selectedCharge.audit_logs?.length > 0 && (
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-sm">Recent Activity</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {selectedCharge.audit_logs.slice(0, 10).map((log, idx) => (
                          <div key={idx} className="text-xs text-white/70 flex justify-between">
                            <span>{log.action}</span>
                            <span className="text-white/50">{new Date(log.timestamp).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Adjust Amount Modal */}
        <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
          <DialogContent className="bg-slate-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Adjust Charge Amount</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">New Amount ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  className="bg-white/10 border-white/20"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Reason (required)</label>
                <Textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Explain the reason for this adjustment..."
                  className="bg-white/10 border-white/20"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
              <Button onClick={handleAdjustAmount} disabled={actionLoading || !adjustReason || adjustReason.length < 10}>
                {actionLoading ? 'Saving...' : 'Save Adjustment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Freeze Modal */}
        <Dialog open={showFreezeModal} onOpenChange={setShowFreezeModal}>
          <DialogContent className="bg-slate-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Freeze Charge</DialogTitle>
            </DialogHeader>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Freeze Reason (required)</label>
              <Textarea
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                placeholder="Explain why this charge is being frozen..."
                className="bg-white/10 border-white/20"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFreezeModal(false)}>Cancel</Button>
              <Button onClick={handleFreeze} disabled={actionLoading || freezeReason.length < 10} className="bg-purple-600 hover:bg-purple-700">
                {actionLoading ? 'Freezing...' : 'Freeze Charge'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Resolved Modal */}
        <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
          <DialogContent className="bg-slate-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Mark as Resolved</DialogTitle>
            </DialogHeader>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Resolution Reason (required)</label>
              <Textarea
                value={resolveReason}
                onChange={(e) => setResolveReason(e.target.value)}
                placeholder="Explain how this charge was resolved (e.g., paid via bank transfer, waived, etc.)..."
                className="bg-white/10 border-white/20"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolveModal(false)}>Cancel</Button>
              <Button onClick={handleMarkResolved} disabled={actionLoading || resolveReason.length < 10} className="bg-cyan-600 hover:bg-cyan-700">
                {actionLoading ? 'Saving...' : 'Mark Resolved'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent className="bg-slate-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Add Internal Note</DialogTitle>
            </DialogHeader>
            <div>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter your note..."
                className="bg-white/10 border-white/20"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
              <Button onClick={handleAddNote} disabled={actionLoading || !noteText}>
                {actionLoading ? 'Saving...' : 'Add Note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* P1: Dispute Detail Modal */}
        <Dialog open={showDisputeModal} onOpenChange={(open) => {
          if (!open) {
            setSelectedDispute(null);
            setResolutionNotes('');
            setPendingResolution(null);
          }
          setShowDisputeModal(open);
        }}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Dispute Details
                {selectedDispute && (
                  <Badge className={`ml-2 ${
                    selectedDispute.status === 'open' ? 'bg-red-500/20 text-red-400' :
                    selectedDispute.status === 'investigating' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {selectedDispute.status?.toUpperCase()}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {disputeDetailLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-white/50" />
              </div>
            ) : selectedDispute && (
              <div className="space-y-6">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-xs text-white/50 mb-1">Dispute ID</p>
                    <p className="text-sm font-mono">#{selectedDispute.dispute_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">Created</p>
                    <p className="text-sm">
                      {selectedDispute.created_at ? new Date(selectedDispute.created_at).toLocaleString() : 'N/A'}
                      {selectedDispute.sla_hours_elapsed && (
                        <span className={`ml-2 text-xs ${selectedDispute.sla_hours_elapsed > 48 ? 'text-red-400' : 'text-white/60'}`}>
                          ({selectedDispute.sla_hours_elapsed?.toFixed(1)}h ago)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* Booking & Vehicle Info */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Booking Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-white/50 mb-1">Booking ID</p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">#{selectedDispute.booking_id?.slice(0, 12)}</span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 text-xs px-2"
                          onClick={() => navigate(`/bookings/${selectedDispute.booking_id}`)}
                        >
                          Open <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                    {selectedDispute.vehicle && (
                      <div>
                        <p className="text-xs text-white/50 mb-1">Vehicle</p>
                        <p>{selectedDispute.vehicle.year} {selectedDispute.vehicle.make} {selectedDispute.vehicle.model}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Parties */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Renter (Disputant)</h4>
                    <p className="text-sm">{selectedDispute.renter?.full_name || 'N/A'}</p>
                    <p className="text-xs text-white/60">{selectedDispute.renter?.email}</p>
                    {selectedDispute.renter?.phone && <p className="text-xs text-white/60">{selectedDispute.renter?.phone}</p>}
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Host</h4>
                    <p className="text-sm">{selectedDispute.host?.full_name || 'N/A'}</p>
                    <p className="text-xs text-white/60">{selectedDispute.host?.email}</p>
                    {selectedDispute.host?.phone && <p className="text-xs text-white/60">{selectedDispute.host?.phone}</p>}
                  </div>
                </div>
                
                {/* Dispute Details */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm font-medium mb-3">Dispute Information</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Amount Disputed:</span>
                      <span className="text-lg font-bold">${selectedDispute.total_dollars?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Category:</span>
                      <Badge className="bg-white/10 text-white capitalize">{selectedDispute.category || selectedDispute.reason_category || 'Other'}</Badge>
                    </div>
                    <div>
                      <p className="text-white/60 mb-1">Reason:</p>
                      <p className="text-sm bg-white/5 p-3 rounded">{selectedDispute.reason}</p>
                    </div>
                  </div>
                </div>
                
                {/* Charges Breakdown (if available from report) */}
                {selectedDispute.report && (
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-sm font-medium mb-3">Charges Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      {selectedDispute.report.fuel_shortfall_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Fuel Shortfall:</span>
                          <span>${selectedDispute.report.fuel_shortfall_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {selectedDispute.report.cleaning_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Cleaning Fee:</span>
                          <span>${selectedDispute.report.cleaning_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {selectedDispute.report.toll_charges > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Toll Charges:</span>
                          <span>${selectedDispute.report.toll_charges?.toFixed(2)}</span>
                        </div>
                      )}
                      {selectedDispute.report.late_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Late Fee:</span>
                          <span>${selectedDispute.report.late_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {selectedDispute.report.excess_km_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Excess KM Fee:</span>
                          <span>${selectedDispute.report.excess_km_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-white/10 pt-2 font-bold">
                        <span>Total:</span>
                        <span>${selectedDispute.report.total_extra_charges?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Evidence Photos */}
                {selectedDispute.evidence_urls && selectedDispute.evidence_urls.length > 0 && (
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-sm font-medium mb-3">Evidence Photos ({selectedDispute.evidence_urls.length})</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedDispute.evidence_urls.map((evidence, idx) => (
                        <a 
                          key={idx} 
                          href={evidence.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block aspect-square rounded overflow-hidden border border-white/10 hover:border-white/30 transition-colors"
                        >
                          <img 
                            src={evidence.url} 
                            alt={`Evidence ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Audit Timeline */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm font-medium mb-3">Timeline</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-white/60">Created:</span>
                      <span>{selectedDispute.created_at ? new Date(selectedDispute.created_at).toLocaleString() : 'N/A'}</span>
                    </div>
                    {selectedDispute.resolved_at && (
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-white/60">Resolved:</span>
                        <span>{new Date(selectedDispute.resolved_at).toLocaleString()}</span>
                        {selectedDispute.resolved_by_email && <span className="text-white/40">by {selectedDispute.resolved_by_email}</span>}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Resolution Section (if already resolved) */}
                {selectedDispute.status === 'resolved' && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <h4 className="text-sm font-medium mb-2 text-green-400">Resolution</h4>
                    <p className="text-sm mb-1"><strong>Outcome:</strong> {selectedDispute.resolution === 'no_charge' ? 'No Charge (Waived)' : 'Payment Required'}</p>
                    {selectedDispute.resolution_notes && (
                      <p className="text-sm text-white/70"><strong>Notes:</strong> {selectedDispute.resolution_notes}</p>
                    )}
                    
                    {/* P1: Payment Status for require_payment resolution */}
                    {selectedDispute.resolution === 'require_payment' && selectedDispute.booking && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-sm mb-1">
                          <strong>Payment Status:</strong>{' '}
                          {selectedDispute.booking.extra_charges_status === 'paid' ? (
                            <Badge className="bg-green-500/30 text-green-400">PAID</Badge>
                          ) : (
                            <Badge className="bg-amber-500/30 text-amber-400">AWAITING PAYMENT</Badge>
                          )}
                        </p>
                        {selectedDispute.booking.extra_charges_payment_intent_id && (
                          <p className="text-xs text-white/50 mt-1">
                            Payment ID: {selectedDispute.booking.extra_charges_payment_intent_id}
                          </p>
                        )}
                        {selectedDispute.booking.extra_charges_paid_at && (
                          <p className="text-xs text-white/50">
                            Paid at: {new Date(selectedDispute.booking.extra_charges_paid_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Resolution Actions (if not resolved) */}
                {selectedDispute.status !== 'resolved' && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <h4 className="text-sm font-medium mb-3 text-amber-400">Take Action</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-white/60 mb-1 block">Resolution Notes * (min 10 chars)</label>
                        <Textarea
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder="Explain your decision and reasoning..."
                          className="bg-white/10 border-white/20 text-sm"
                          rows={3}
                          data-testid="resolution-notes-textarea"
                        />
                        <p className="text-xs text-white/40 mt-1">{resolutionNotes.length}/10 characters minimum</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          onClick={() => initiateResolution('require_payment')}
                          disabled={resolutionNotes.trim().length < 10 || actionLoading}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="modal-require-payment-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Require Payment
                        </Button>
                        <Button 
                          onClick={() => initiateResolution('no_charge')}
                          disabled={resolutionNotes.trim().length < 10 || actionLoading}
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                          data-testid="modal-resolve-no-charge-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Resolve (No Charge)
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDisputeModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* P1: Resolution Confirmation Dialog */}
        <Dialog open={showResolveConfirm} onOpenChange={setShowResolveConfirm}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Confirm Resolution
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-white/80 mb-4">
                {pendingResolution?.type === 'no_charge' 
                  ? 'This will waive all charges and mark the booking as completed. The renter will not be required to pay.'
                  : 'This will uphold the charges and require the renter to pay. They will be notified to complete payment.'}
              </p>
              <p className="text-xs text-amber-400">
                ⚠️ This action is final and will update booking/payment state.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolveConfirm(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleResolveDisputeFromModal}
                disabled={actionLoading}
                className={pendingResolution?.type === 'no_charge' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                data-testid="confirm-resolution-btn"
              >
                {actionLoading ? 'Processing...' : 'Confirm Resolution'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Detail Modal - Ledger Breakdown */}
        <Dialog 
          open={showPaymentModal} 
          onOpenChange={(open) => {
            setShowPaymentModal(open);
            // Clear selectedPayment when modal closes to prevent stale data
            if (!open) {
              setSelectedPayment(null);
            }
          }}
        >
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`payment-modal-${selectedPayment?.row_id || 'loading'}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <DollarSign className={`w-5 h-5 ${selectedPayment?.type === 'bond' ? 'text-amber-400' : 'text-emerald-400'}`} />
                <span>{selectedPayment?.type === 'bond' ? 'Bond Payment Details' : 'Rental Payment Details'}</span>
                {selectedPayment?.type === 'bond' && (
                  <Badge className="bg-amber-600/60 text-amber-100 text-xs ml-2">BOND</Badge>
                )}
                {selectedPayment?.stripe_mode && (
                  <Badge 
                    className={`text-xs ml-2 ${selectedPayment.stripe_mode === 'TEST' ? 'bg-amber-600/60 text-amber-100' : 'bg-green-600/60 text-green-100'}`}
                  >
                    {selectedPayment.payment_provider || 'airwallex'}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {paymentDetailLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
              </div>
            ) : selectedPayment ? (
              <div className="space-y-6" data-testid={`payment-detail-modal-${selectedPayment.type}`}>
                {/* Row ID & Type Info */}
                <div className="bg-slate-800/60 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">Row ID</p>
                      <p className="text-xs font-mono text-slate-300">{selectedPayment.row_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Payment Type</p>
                      <Badge className={selectedPayment.type === 'bond' ? 'bg-amber-700/80 text-amber-100' : 'bg-emerald-700/80 text-emerald-100'}>
                        {selectedPayment.type?.toUpperCase()}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Booking ID</p>
                      <p className="text-sm font-mono text-white">{selectedPayment.booking_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Status</p>
                      <Badge className="bg-slate-700/80 text-white">{selectedPayment.status}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Created At</p>
                      <p className="text-sm text-white">{selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleString() : '—'}</p>
                    </div>
                    {selectedPayment.type !== 'bond' && (
                      <div>
                        <p className="text-xs text-slate-400">Ledger Status</p>
                        <p className="text-sm text-white">{selectedPayment.has_ledger ? 'Stored' : 'Estimated'}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* BOND-SPECIFIC DISPLAY */}
                {selectedPayment.type === 'bond' ? (
                  <>
                    {/* Bond Info */}
                    <div className={`rounded-lg p-4 border ${
                      selectedPayment.bond_status === 'captured' || selectedPayment.bond_status === 'partially_captured'
                        ? 'bg-red-900/30 border-red-500/40'
                        : selectedPayment.bond_status === 'released'
                          ? 'bg-green-900/30 border-green-500/40'
                          : selectedPayment.bond_status === 'superseded' || selectedPayment.bond_status === 'cancelled'
                            ? 'bg-slate-800/60 border-slate-500/40'
                            : 'bg-amber-900/30 border-amber-500/40'
                    }`}>
                      <h4 className={`text-sm font-semibold mb-3 ${
                        selectedPayment.bond_status === 'captured' || selectedPayment.bond_status === 'partially_captured'
                          ? 'text-red-300'
                          : selectedPayment.bond_status === 'released'
                            ? 'text-green-300'
                            : selectedPayment.bond_status === 'superseded' || selectedPayment.bond_status === 'cancelled'
                              ? 'text-slate-300'
                              : 'text-amber-300'
                      }`}>
                        {selectedPayment.payment_type === 'extension_bond' ? 'Extension Bond' : 'Security Deposit (Bond)'}
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-white/10">
                          <span className="text-sm text-slate-200">Bond Amount</span>
                          <span className="text-xl font-bold text-white" data-testid="bond-amount">
                            ${selectedPayment.amount_dollars?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-white/10">
                          <span className="text-sm text-slate-200">Bond Type</span>
                          <Badge className={selectedPayment.payment_type === 'extension_bond' ? 'bg-purple-700/60 text-purple-100' : 'bg-amber-700/60 text-amber-100'}>
                            {selectedPayment.payment_type === 'extension_bond' ? 'EXTENSION BOND' : 'BOOKING BOND'}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-white/10">
                          <span className="text-sm text-slate-200">Authorization Mode</span>
                          <Badge className="bg-slate-700/60 text-slate-100">
                            {selectedPayment.bond_mode?.toUpperCase() || 'STANDARD'}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-white/10">
                          <span className="text-sm text-slate-200">Bond Lifecycle Status</span>
                          <Badge className={`text-sm ${
                            selectedPayment.bond_status === 'authorized' ? 'bg-amber-600/60 text-amber-100' :
                            selectedPayment.bond_status === 'captured' ? 'bg-red-600/60 text-red-100' :
                            selectedPayment.bond_status === 'partially_captured' ? 'bg-orange-600/60 text-orange-100' :
                            selectedPayment.bond_status === 'released' ? 'bg-green-600/60 text-green-100' :
                            selectedPayment.bond_status === 'superseded' ? 'bg-slate-600/60 text-slate-100' :
                            selectedPayment.bond_status === 'cancelled' ? 'bg-slate-600/60 text-slate-100' :
                            selectedPayment.bond_status === 'pending' ? 'bg-blue-600/60 text-blue-100' :
                            'bg-slate-600/60 text-slate-100'
                          }`}>
                            {selectedPayment.bond_status === 'authorized' ? 'HOLD ACTIVE' :
                             selectedPayment.bond_status === 'captured' ? 'CAPTURED' :
                             selectedPayment.bond_status === 'partially_captured' ? 'PARTIAL CAPTURE' :
                             selectedPayment.bond_status === 'released' ? 'RELEASED (Returned)' :
                             selectedPayment.bond_status === 'superseded' ? 'SUPERSEDED' :
                             selectedPayment.bond_status === 'cancelled' ? 'CANCELLED' :
                             selectedPayment.bond_status === 'pending' ? 'PENDING AUTH' :
                             selectedPayment.bond_status?.toUpperCase() || 'UNKNOWN'}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-slate-200">Money Direction</span>
                          <span className={`text-sm font-medium ${
                            selectedPayment.bond_status === 'authorized' ? 'text-amber-400' :
                            selectedPayment.bond_status === 'captured' ? 'text-red-400' :
                            selectedPayment.bond_status === 'released' ? 'text-green-400' :
                            'text-slate-400'
                          }`}>
                            {selectedPayment.bond_status === 'authorized' ? '→ Held from renter (escrow)' :
                             selectedPayment.bond_status === 'captured' ? 'Captured from renter' :
                             selectedPayment.bond_status === 'partially_captured' ? '→ Partial charge to renter' :
                             selectedPayment.bond_status === 'released' ? '← Returned to renter' :
                             selectedPayment.bond_status === 'superseded' ? '→ Replaced by new bond' :
                             selectedPayment.bond_status === 'cancelled' ? '← Authorization voided' :
                             selectedPayment.bond_status === 'pending' ? '⏳ Awaiting card authorization' :
                             '— Status unknown'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Lifecycle Status Notice */}
                      <div className={`mt-4 p-3 rounded border ${
                        selectedPayment.bond_status === 'captured' || selectedPayment.bond_status === 'partially_captured'
                          ? 'bg-red-950/50 border-red-700/50'
                          : selectedPayment.bond_status === 'released'
                            ? 'bg-green-950/50 border-green-700/50'
                            : 'bg-slate-900/50 border-slate-700/50'
                      }`}>
                        <p className="text-xs text-slate-300">
                          {selectedPayment.bond_status === 'authorized' && (
                            <><strong className="text-amber-300">HOLD ACTIVE:</strong> This bond is being held as a security deposit. The renter's card has been authorised and funds are reserved. The hold will be released back to the renter after successful trip completion, unless a claim is submitted.</>
                          )}
                          {selectedPayment.bond_status === 'captured' && (
                            <><strong className="text-red-300">CAPTURED:</strong> This bond has been captured from the renter's card, typically due to a damage claim or policy violation.</>
                          )}
                          {selectedPayment.bond_status === 'partially_captured' && (
                            <><strong className="text-orange-300">PARTIAL CAPTURE:</strong> A portion of this bond has been captured. The remaining amount will be released back to the renter.</>
                          )}
                          {selectedPayment.bond_status === 'released' && (
                            <><strong className="text-green-300">RELEASED:</strong> This bond hold has been released. The authorization has been voided and no charge was made to the renter. Trip completed successfully.</>
                          )}
                          {selectedPayment.bond_status === 'superseded' && (
                            <><strong className="text-slate-300">SUPERSEDED:</strong> This bond has been replaced by a newer bond (e.g., extension increased the required bond). The original authorization was cancelled.</>
                          )}
                          {selectedPayment.bond_status === 'cancelled' && (
                            <><strong className="text-slate-300">CANCELLED:</strong> This bond authorization was cancelled before capture. No money was transferred.</>
                          )}
                          {selectedPayment.bond_status === 'pending' && (
                            <><strong className="text-blue-300">PENDING:</strong> Awaiting card authorization from the renter's bank. The hold has not yet been confirmed.</>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Bond Lifecycle Chain - Visual representation */}
                    {selectedPayment.bond_lifecycle && (
                      <div className={`rounded-lg p-4 border ${
                        selectedPayment.bond_lifecycle.is_superseded 
                          ? 'bg-slate-800/40 border-slate-600/50'
                          : selectedPayment.bond_lifecycle.is_active
                            ? 'bg-purple-900/30 border-purple-500/50'
                            : 'bg-slate-800/60 border-slate-600/50'
                      }`}>
                        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 text-purple-400" />
                          Bond Lifecycle Chain
                        </h4>
                        
                        {/* Lifecycle visualization */}
                        <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
                          {/* Original Bond */}
                          <div className={`px-3 py-2 rounded-lg border text-center min-w-[100px] ${
                            selectedPayment.payment_type !== 'extension_bond' && !selectedPayment.bond_lifecycle.is_superseded
                              ? 'bg-amber-600/30 border-amber-500/60'
                              : selectedPayment.bond_lifecycle.supersedes
                                ? 'bg-slate-700/50 border-slate-600/50 opacity-60'
                                : 'bg-amber-600/30 border-amber-500/60'
                          }`}>
                            <p className={`text-xs font-medium ${
                              selectedPayment.bond_lifecycle.supersedes ? 'line-through text-slate-500' : 'text-amber-300'
                            }`}>
                              Original Bond
                            </p>
                            {selectedPayment.bond_lifecycle.supersedes && (
                              <Badge className="text-[10px] bg-slate-600/60 text-slate-300 mt-1">CANCELLED</Badge>
                            )}
                            {selectedPayment.payment_type !== 'extension_bond' && selectedPayment.bond_lifecycle.is_active && (
                              <Badge className="text-[10px] bg-amber-600/60 text-amber-100 mt-1">ACTIVE</Badge>
                            )}
                          </div>
                          
                          {/* Arrow indicator */}
                          {(selectedPayment.bond_lifecycle.supersedes || selectedPayment.bond_lifecycle.is_superseded) && (
                            <div className="flex items-center text-slate-500">
                              <span className="text-lg">→</span>
                            </div>
                          )}
                          
                          {/* Extension Bond (if applicable) */}
                          {(selectedPayment.payment_type === 'extension_bond' || selectedPayment.bond_lifecycle.is_superseded) && (
                            <div className={`px-3 py-2 rounded-lg border text-center min-w-[100px] ${
                              selectedPayment.payment_type === 'extension_bond' && selectedPayment.bond_lifecycle.is_active
                                ? 'bg-purple-600/40 border-purple-500/70 ring-2 ring-purple-400/50'
                                : selectedPayment.bond_lifecycle.is_superseded
                                  ? 'bg-purple-600/40 border-purple-500/70 ring-2 ring-purple-400/50'
                                  : 'bg-purple-600/30 border-purple-500/60'
                            }`}>
                              <p className="text-xs font-medium text-purple-300">
                                Extension Bond
                              </p>
                              {selectedPayment.payment_type === 'extension_bond' && selectedPayment.bond_lifecycle.is_active && (
                                <Badge className="text-[10px] bg-purple-600/60 text-purple-100 mt-1">CURRENT</Badge>
                              )}
                              {selectedPayment.bond_lifecycle.is_superseded && (
                                <Badge className="text-[10px] bg-purple-600/60 text-purple-100 mt-1">ACTIVE</Badge>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Lifecycle explanation */}
                        <div className={`p-3 rounded border ${
                          selectedPayment.bond_lifecycle.is_superseded 
                            ? 'bg-slate-900/50 border-slate-700/50'
                            : 'bg-purple-950/30 border-purple-700/40'
                        }`}>
                          <p className="text-xs text-slate-300">
                            {selectedPayment.bond_lifecycle.lifecycle_note || (
                              selectedPayment.bond_lifecycle.is_active 
                                ? 'This is the currently active bond for this booking.'
                                : 'Bond lifecycle information'
                            )}
                          </p>
                          {selectedPayment.bond_lifecycle.is_superseded && (
                            <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              No additional funds are being held — extension replaces original bond
                            </p>
                          )}
                          {selectedPayment.payment_type === 'extension_bond' && selectedPayment.bond_lifecycle.supersedes && (
                            <p className="text-xs text-purple-300 mt-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              This extension bond replaced the original booking bond
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Bond Financial Summary - Show actual impact */}
                    <div className={`rounded-lg p-4 ${
                      selectedPayment.bond_status === 'captured' || selectedPayment.bond_status === 'partially_captured' 
                        ? 'bg-slate-800/60' 
                        : 'bg-slate-800/60 opacity-60'
                    }`}>
                      <h4 className="text-sm font-semibold text-slate-400 mb-3">
                        Financial Impact 
                        {(selectedPayment.bond_status !== 'captured' && selectedPayment.bond_status !== 'partially_captured') && ' (Bonds = No Revenue)'}
                      </h4>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="p-2 bg-slate-900/50 rounded">
                          <p className="text-xs text-slate-500">Platform Fee</p>
                          <p className="text-sm font-mono text-slate-400">—</p>
                        </div>
                        <div className="p-2 bg-slate-900/50 rounded">
                          <p className="text-xs text-slate-500">Processing Fee</p>
                          <p className="text-sm font-mono text-slate-400">—</p>
                        </div>
                        <div className="p-2 bg-slate-900/50 rounded">
                          <p className="text-xs text-slate-500">Host Payout</p>
                          <p className="text-sm font-mono text-slate-400">—</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* RENTAL-SPECIFIC DISPLAY */}
                    {/* SECTION 1: HireHub Business Values (Fee Snapshot) */}
                    <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-300 mb-1 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        HireHub Business Values
                      </h4>
                      <p className="text-xs text-blue-400/70 mb-3">Fee snapshot captured at booking time - used for payouts & reconciliation</p>
                      
                      {selectedPayment.is_estimated && (
                        <div className="bg-amber-900/40 border border-amber-500/40 rounded p-3 mb-4">
                          <p className="text-xs text-amber-300">
                            ⚠️ Legacy payment - no detailed ledger available. Showing estimated values.
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        {/* Booking Subtotal */}
                        <div className="flex justify-between items-center py-2 border-b border-blue-700/30">
                          <span className="text-sm text-blue-200">Booking Subtotal</span>
                          <span className="text-lg font-bold text-white" data-testid="payment-gross">
                            ${selectedPayment.amount_dollars?.toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Fee Snapshot */}
                        <div className="flex justify-between items-center py-2 border-b border-blue-700/30">
                          <span className="text-sm text-blue-200">Platform Fee Snapshot</span>
                          <div className="text-right">
                            <span className="text-lg font-semibold text-emerald-400" data-testid="payment-platform-fee">
                              ${selectedPayment.platform_fee_dollars?.toFixed(2)}
                            </span>
                            <span className="text-xs text-blue-400/60 block">
                              ({selectedPayment.platform_fee_pct ? `${(selectedPayment.platform_fee_pct * 100).toFixed(1)}%` : '17%'})
                            </span>
                          </div>
                        </div>
                        
                        {/* Bond Mode */}
                        {selectedPayment.bond_mode && selectedPayment.bond_mode !== 'none' && (
                          <div className="flex justify-between items-center py-2 border-b border-blue-700/30">
                            <span className="text-sm text-blue-200">Bond Mode</span>
                            <Badge className="bg-amber-600/40 text-amber-200">
                              {selectedPayment.bond_mode?.toUpperCase()}
                            </Badge>
                          </div>
                        )}
                        
                        {/* Host Payout (Calculated) */}
                        <div className="flex justify-between items-center py-2 bg-cyan-900/30 rounded px-3">
                          <span className="text-sm font-medium text-cyan-300">Host Payout (Calculated)</span>
                          <span className="text-lg font-bold text-cyan-200" data-testid="payment-host-payout">
                            ${selectedPayment.host_payout_dollars?.toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Platform Net Earnings */}
                        <div className="flex justify-between items-center py-2 bg-green-900/30 rounded px-3">
                          <span className="text-sm font-medium text-green-300">Platform Net Earnings</span>
                          <span className="text-lg font-bold text-green-200" data-testid="payment-platform-net">
                            ${selectedPayment.platform_net_dollars?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* SECTION 2: Provider Payment Values */}
                    <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-purple-300 mb-1 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Payment Provider Values
                      </h4>
                      <p className="text-xs text-purple-400/70 mb-3">Actual amounts from payment provider — use for reconciliation</p>
                      
                      <div className="space-y-2">
                        {/* Charge Amount */}
                        <div className="flex justify-between items-center py-2 border-b border-purple-700/30">
                          <span className="text-sm text-purple-200">Charge Amount</span>
                          <span className="text-lg font-bold text-white">
                            ${selectedPayment.stripe_charge_amount_dollars?.toFixed(2) || selectedPayment.amount_dollars?.toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Processing Fee */}
                        <div className="flex justify-between items-center py-2 border-b border-purple-700/30">
                          <span className="text-sm text-purple-200">Processing Fee</span>
                          <span className="text-sm font-medium text-red-400" data-testid="payment-processing-fee-total">
                            ${(selectedPayment.processing_fee_dollars ?? selectedPayment.stripe_fee_dollars)?.toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Net After Fees */}
                        <div className="flex justify-between items-center py-2 border-b border-purple-700/30">
                          <span className="text-sm text-purple-200">Net (After Fees)</span>
                          <span className="text-sm font-medium text-purple-300">
                            ${((selectedPayment.stripe_charge_amount_dollars || selectedPayment.amount_dollars || 0) - (selectedPayment.processing_fee_dollars ?? selectedPayment.stripe_fee_dollars ?? 0)).toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Fee Assignment */}
                        <div className="bg-slate-900/40 rounded p-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-400">Processing Fee Paid By</span>
                            <Badge className={selectedPayment.fee_policy_version?.includes('renter-pays')
                              ? 'bg-green-600/60 text-green-200 text-xs'
                              : 'bg-amber-600/60 text-amber-200 text-xs'
                            }>
                              {selectedPayment.fee_policy_version?.includes('renter-pays') ? 'Renter (New Policy)' : 'Host (Legacy)'}
                            </Badge>
                          </div>
                          {(selectedPayment.processing_fee_host_share_cents ?? selectedPayment.stripe_fee_host_share_cents) > 0 && (
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-xs text-slate-500">Host's Share of Processing Fee</span>
                              <span className="text-xs text-red-300">
                                {formatCentsAUD(selectedPayment.processing_fee_host_share_cents ?? selectedPayment.stripe_fee_host_share_cents)}
                              </span>
                            </div>
                          )}
                          {selectedPayment.fee_policy_version?.includes('renter-pays') && (
                            <p className="text-xs text-green-400/70 mt-2">
                              New fee policy: Renter pays processing fee. Host payout not reduced.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* SECTION 3: Payment Object IDs */}
                <div className="bg-slate-800/60 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-slate-300 mb-1 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Payment Object IDs
                    <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                      {selectedPayment.payment_provider || 'airwallex'}
                    </Badge>
                  </h4>
                  <p className="text-xs text-slate-500 mb-3">Reference IDs for payment provider reconciliation</p>
                  
                  <div className="grid grid-cols-1 gap-3 text-xs">
                    {selectedPayment.stripe_payment_intent_id && (
                      <div className="flex items-center justify-between bg-slate-700/50 p-2 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-500 block text-xs">Payment Intent ID</span>
                          <span className="font-mono text-slate-300 text-xs truncate block">{selectedPayment.stripe_payment_intent_id}</span>
                        </div>
                      </div>
                    )}
                    {selectedPayment.stripe_charge_id && (
                      <div className="flex items-center justify-between bg-slate-700/50 p-2 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-500 block text-xs">Charge ID</span>
                          <span className="font-mono text-slate-300 text-xs truncate block">{selectedPayment.stripe_charge_id}</span>
                        </div>
                      </div>
                    )}
                    {selectedPayment.stripe_balance_txn_id && (
                      <div className="flex items-center justify-between bg-slate-700/50 p-2 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-500 block text-xs">Balance Transaction ID</span>
                          <span className="font-mono text-slate-300 text-xs truncate block">{selectedPayment.stripe_balance_txn_id}</span>
                        </div>
                      </div>
                    )}
                    {selectedPayment.refund_id && (
                      <div className="flex items-center justify-between bg-slate-700/50 p-2 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-500 block text-xs">Refund ID</span>
                          <span className="font-mono text-slate-300 text-xs truncate block">{selectedPayment.refund_id}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payout Status (Delayed Payouts) - Only for rentals */}
                {selectedPayment.type !== 'bond' && selectedPayment.payout_status && (
                  <div className="bg-slate-800/60 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">Payout Status</h4>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge className={`
                        ${selectedPayment.payout_status === 'paid' ? 'bg-green-600/60 text-green-100' : ''}
                        ${selectedPayment.payout_status === 'eligible' ? 'bg-amber-600/60 text-amber-100' : ''}
                        ${selectedPayment.payout_status === 'not_eligible' ? 'bg-slate-600/60 text-slate-300' : ''}
                        ${selectedPayment.payout_status === 'failed' ? 'bg-red-600/60 text-red-100' : ''}
                      `}>
                        {selectedPayment.payout_status?.toUpperCase()}
                      </Badge>
                      {selectedPayment.payout_transfer_id && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            Transfer: <span className="font-mono">{selectedPayment.payout_transfer_id}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-slate-400 py-8">No payment data available</p>
            )}
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPayment(null);  // Clear state on close
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </PlatformOwnerAppShell>
  );
};

export default FinancialEnforcementDashboard;
